/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useCallback, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';

/* Import configuration */
import { APP_ROUTES } from '@/config/appRoutes';
import { PLOTLY_GROUP_OPTIONS } from '@/config/eeCheckConfig';
import { DEMO_CSV_USERNAME } from '@/config/csvSensors';
import { refreshSensorCatalog } from '@/config/sensorCatalog';

/* Import types */
import type { DebugRequestEntry } from '@/types/ui';
import type { LocalizedMessage } from '@/types/i18n';

/* Import components */
import AppFooter from '@/components/layout/AppFooter';
import AppNavbar from '@/components/layout/AppNavbar';
import AppRoutes from '@/components/layout/AppRoutes';
import { APP_ROUTE_TABS, createAppNavigation, getAppPageContext } from '@/components/layout/appNavigation';
import StatusModal from '@/components/feedback/StatusModal';
import { DashboardLayoutProvider, type DashboardLayoutBreakpoint } from '@/context/DashboardLayoutContext';
import { useI18n } from '@/context/I18nContext';
import { OverviewDisplayProvider } from '@/context/OverviewDisplayContext';
import { SessionProvider } from '@/context/SessionContext';
import { TechnicalDetailsProvider } from '@/context/TechnicalDetailsContext';

/* Import libraries */
import { toDebugString } from '@/lib/app/debugRequests';
import { storeLoginRedirect } from '@/lib/app/sessionState';
import { useApplicationErrorLogCount } from '@/hooks/useApplicationErrorLogCount';
import { useAppPreferences } from '@/hooks/useAppPreferences';
import { useAppModals } from '@/hooks/useAppModals';
import { useAppSession } from '@/hooks/useAppSession';
import { useDemoOutputs } from '@/hooks/useDemoOutputs';
import { useDemoRequests } from '@/hooks/useDemoRequests';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useCurrentValueDemo } from '@/hooks/useCurrentValueDemo';
import { useIntervalValueDemo } from '@/hooks/useIntervalValueDemo';
import { usePlotlyDemo } from '@/hooks/usePlotlyDemo';
import { useTimeSeriesDemo } from '@/hooks/useTimeSeriesDemo';
import {logDebug} from "@/lib/app/debugLog";

function App() {

    logDebug('info', 'SÜ BK = Systemübersicht Berechnungskette');

    /* --------------------
       Core app state (routing, request feedback, debug history, and dashboard layout)
       -------------------- */
    const { language, setLanguage, t } = useI18n();
    const location = useLocation();
    const navigate = useNavigate();
    const [requestStatus, setRequestStatus] = useState<LocalizedMessage>({ key: 'No API request executed yet.' });
    const [debugEntries, setDebugEntries] = useState<DebugRequestEntry[]>([]);
    const [sensorCatalogVersion, setSensorCatalogVersion] = useState(0);
    const [dashboardDesktopBreakpoint, setDashboardDesktopBreakpoint] = useState<DashboardLayoutBreakpoint | null>(null);
    const errorLogCount = useApplicationErrorLogCount();

    /* --------------------
       Demo response data (parsed output, raw responses, and reset handling)
       -------------------- */
    const demoOutputs = useDemoOutputs();
    const {
        currentValueOutputState,
        intervalValueOutputState,
        loginOutputState,
        outputMode,
        plotlyFigure,
        resetDemoOutputs,
        timeSeriesOutputState,
    } = demoOutputs;
    const handleResetAppView = useCallback(() => {
        resetDemoOutputs();
        setRequestStatus({ key: 'No API request executed yet.' });
    }, [resetDemoOutputs]);
    const handleCatalogChanged = useCallback((username: string) => {
        refreshSensorCatalog(username);
        setSensorCatalogVersion((version) => version + 1);
    }, []);

    /* --------------------
       Global feedback modal
       -------------------- */
    const { statusModal } = useAppModals(t);

    /* --------------------
       Session lifecycle (login, persistence, and expiration handling)
       -------------------- */
    const {
        clearSessionState,
        handleClearSession,
        handleLoginSubmit,
        handleSessionExpired,
        isSessionExpired,
        isSessionStateReady,
        isSubmittingLogin,
        loginErrorMessage,
        accessToken,
        sessionImportedAt,
        sessionOrigin,
        sessionStatus,
        sessionUsername,
        setSessionStatus,
    } = useAppSession({
        navigate,
        onCatalogChanged: handleCatalogChanged,
        onResetAppView: handleResetAppView,
        setDebugEntries,
        t,
    });

    /* --------------------
       App preferences (persisted theme and optional UI sections)
       -------------------- */
    const {
        isAdvancedViewEnabled: storedIsAdvancedViewEnabled,
        isDebugSectionEnabled: storedIsDebugSectionEnabled,
        isDemoSectionEnabled: storedIsDemoSectionEnabled,
        isTechnicalDetailsEnabled: storedIsTechnicalDetailsEnabled,
        isToolsSectionEnabled: storedIsToolsSectionEnabled,
        setIsAdvancedViewEnabled,
        setIsDebugSectionEnabled,
        setIsDemoSectionEnabled,
        setIsTechnicalDetailsEnabled,
        setIsToolsSectionEnabled,
        setTheme,
        theme,
    } = useAppPreferences();
    // The demo account must not regain developer-only access through stale localStorage values.
    const canAccessDeveloperOptions = sessionUsername !== DEMO_CSV_USERNAME;
    const isAdvancedViewEnabled = canAccessDeveloperOptions && storedIsAdvancedViewEnabled;
    const isDebugSectionEnabled = canAccessDeveloperOptions && storedIsDebugSectionEnabled;
    const isDemoSectionEnabled = canAccessDeveloperOptions && storedIsDemoSectionEnabled;
    const isTechnicalDetailsEnabled = canAccessDeveloperOptions && storedIsTechnicalDetailsEnabled;
    const isToolsSectionEnabled = canAccessDeveloperOptions && storedIsToolsSectionEnabled;

    /* --------------------
       Plotly demo selection and request payload
       -------------------- */
    const {
        activePlotlyGroup,
        activePlotlyRangeOptions,
        activePlotlyReferences,
        activePlotlySubsensorOptions,
        plotlyRequest,
        plotlyRequestMeter,
        selectedGroup,
        selectedMeter,
        selectedRange,
        selectedReference,
        selectedSensorDescription,
        selectedSubsensor,
        setSelectedGroup,
        setSelectedMeter,
        setSelectedRange,
        setSelectedReference,
        setSelectedSubsensor,
    } = usePlotlyDemo(sensorCatalogVersion);

    /* --------------------
       Time series demo selection and request payload
       -------------------- */
    const {
        deviceMetadata: timeSeriesDeviceMetadata,
        deviceSensor: timeSeriesDeviceSensor,
        deviceSensorCatalog: timeSeriesDeviceSensorCatalog,
        selectedTimeSeriesAvailability,
        selectedTimeSeriesGroup,
        selectedTimeSeriesGroupFilter,
        selectedTimeSeriesMeter,
        selectedTimeSeriesSubsensor,
        selectedTimeSeriesVirtualDeviceGroupBy,
        setSelectedTimeSeriesAvailability,
        setSelectedTimeSeriesGroup,
        setSelectedTimeSeriesGroupFilter,
        setSelectedTimeSeriesMeter,
        setSelectedTimeSeriesSubsensor,
        setSelectedTimeSeriesVirtualDeviceGroupBy,
        setTimeSeriesFrom,
        setTimeSeriesTarget,
        setTimeSeriesTo,
        setTimeSeriesToMode,
        timeSeriesFrom,
        timeSeriesRequest,
        timeSeriesSensorTargets,
        timeSeriesTarget,
        timeSeriesTo,
        timeSeriesToMode,
        timeSeriesVirtualDevices,
    } = useTimeSeriesDemo(sensorCatalogVersion);

    /* --------------------
       Current value demo selection, targets, and request payload
       -------------------- */
    const {
        currentValueRequest,
        currentValueTarget,
        currentValueTargets,
        currentValueTimestamp,
        currentValueTimestampMode,
        deviceMetadata: currentValueDeviceMetadata,
        deviceSensor: currentValueDeviceSensor,
        deviceSensorCatalog: currentValueDeviceSensorCatalog,
        selectedAvailability: currentValueAvailability,
        selectedCustomer: currentValueCustomer,
        selectedDevice: currentValueDevice,
        selectedGroupBy: currentValueGroupBy,
        selectedGroupFilter: currentValueGroupFilter,
        selectedSensorTarget: currentValueSensorTarget,
        sensorTargets: currentValueSensorTargets,
        setSelectedAvailability: setCurrentValueAvailability,
        setSelectedCustomer: setCurrentValueCustomer,
        setSelectedDevice: setCurrentValueDevice,
        setSelectedGroupBy: setCurrentValueGroupBy,
        setSelectedGroupFilter: setCurrentValueGroupFilter,
        setSelectedSensorTarget: setCurrentValueSensorTarget,
        setCurrentValueTargets,
        setCurrentValueTimestamp,
        setCurrentValueTimestampMode,
        virtualDevices: currentValueVirtualDevices,
    } = useCurrentValueDemo(sensorCatalogVersion);

    /* --------------------
       Interval value demo selection, targets, and request payload
       -------------------- */
    const {
        intervalValueFrom,
        intervalValueInterval,
        intervalValueRequest,
        intervalValueTarget,
        intervalValueTargets,
        intervalValueTo,
        deviceMetadata: intervalValueDeviceMetadata,
        deviceSensor: intervalValueDeviceSensor,
        deviceSensorCatalog: intervalValueDeviceSensorCatalog,
        selectedAvailability: intervalValueAvailability,
        selectedCustomer: intervalValueCustomer,
        selectedDevice: intervalValueDevice,
        selectedGroupBy: intervalValueGroupBy,
        selectedGroupFilter: intervalValueGroupFilter,
        selectedSensorTarget: intervalValueSensorTarget,
        sensorTargets: intervalValueSensorTargets,
        setSelectedAvailability: setIntervalValueAvailability,
        setSelectedCustomer: setIntervalValueCustomer,
        setSelectedDevice: setIntervalValueDevice,
        setSelectedGroupBy: setIntervalValueGroupBy,
        setSelectedGroupFilter: setIntervalValueGroupFilter,
        setSelectedSensorTarget: setIntervalValueSensorTarget,
        setIntervalValueFrom,
        setIntervalValueInterval,
        setIntervalValueTargets,
        setIntervalValueTo,
        virtualDevices: intervalValueVirtualDevices,
    } = useIntervalValueDemo(sensorCatalogVersion);

    /* --------------------
       Demo API requests (requests, parsed responses, debug history, and feedback)
       -------------------- */
    const {
        handleAddCurrentValueTarget,
        handleAddIntervalValueTarget,
        handleCheckSession,
        handleCurrentValueRequest,
        handleExampleRequest,
        handleIntervalValueRequest,
        handleRemoveCurrentValueTarget,
        handleRemoveIntervalValueTarget,
        handleTimeSeriesRequest,
        isCheckingSession,
        isRequestingCurrentValue,
        isRequestingData,
        isRequestingIntervalValues,
        isRequestingSeries,
    } = useDemoRequests({
        clearSessionState,
        currentValueRequest,
        currentValueTargets,
        intervalValueFrom,
        intervalValueRequest,
        intervalValueTargets,
        intervalValueTo,
        navigate,
        outputs: demoOutputs,
        plotlyRequest,
        accessToken,
        setCurrentValueTargets,
        setDebugEntries,
        setIntervalValueTargets,
        setRequestStatus,
        setSessionStatus,
        onSessionExpired: handleSessionExpired,
        showStatusModal: statusModal.show,
        t,
        timeSeriesRequest,
    });

    /* --------------------
       Derived labels (translated values shared by the login and demo pages)
       -------------------- */
    const sessionStatusText = t(sessionStatus.key, sessionStatus.values);
    const requestStatusText = t(requestStatus.key, requestStatus.values);

    /* --------------------
       Page context and navigation (derived from the current route)
       -------------------- */
    const hasSession = Boolean(accessToken);
    const pageContext = getAppPageContext({ pathname: location.pathname, t });
    const {
        activeTab,
        currentPageBreadcrumbItems,
        isDashboardMenuActive,
        isDashboardPage,
        isDebugMenuActive,
        isDemoMenuActive,
        isToolsMenuActive,
        isLoginPage,
        isPublicInfoPage,
        isPublicRoute,
    } = pageContext;
    const {
        dashboardMenuItems,
        debugMenuItems,
        demoMenuItems,
        toolsMenuItems,
    } = createAppNavigation({
        debugEntryCount: debugEntries.length,
        hasSession,
        t,
    });
    const dashboardMainBreakpointClassName = isDashboardPage && dashboardDesktopBreakpoint
        ? `app-main--dashboard-${dashboardDesktopBreakpoint}`
        : '';
    const dashboardContentBreakpointClassName = isDashboardPage && dashboardDesktopBreakpoint
        ? `app-content--dashboard-${dashboardDesktopBreakpoint}`
        : '';
    const loginStatusLabel = hasSession ? t('Logged in') : t('Logged out');

    /* Keep the browser title aligned with the active breadcrumb path. */
    useDocumentTitle({
        activeTab,
        breadcrumbItems: currentPageBreadcrumbItems,
        hasSession,
    });

    /* --------------------
       Route access redirects (session state, public routes, and protected debug history)
       -------------------- */
    useEffect(() => {
        if (!isSessionStateReady) {
            return;
        }

        if (!accessToken) {
            if (!isPublicRoute) {
                storeLoginRedirect(`${location.pathname}${location.search}`);
                navigate(APP_ROUTES.login, { replace: true });
            }
            return;
        }

        if (sessionOrigin && isLoginPage) {
            navigate(APP_ROUTES.home, { replace: true });
            return;
        }

        if (activeTab === 'debug' && debugEntries.length === 0) {
            navigate(APP_ROUTES.home, { replace: true });
        }
    }, [
        activeTab,
        debugEntries.length,
        isLoginPage,
        isPublicRoute,
        isSessionStateReady,
        navigate,
        accessToken,
        sessionOrigin,
    ]);

    /* --------------------
       Navbar cleanup (close an open dropdown after route navigation)
       -------------------- */
    useEffect(() => {
        const activeElement = document.activeElement;

        if (
            activeElement instanceof HTMLElement &&
            activeElement.closest('.app-navbar .dropdown')
        ) {
            activeElement.blur();
        }
    }, [location.pathname]);

    /* --------------------
       Route component props (prepared here and rendered by AppRoutes)
       -------------------- */
    const loginPageProps = {
        errorMessage: loginErrorMessage,
        isSubmitting: isSubmittingLogin,
        onSubmit: handleLoginSubmit,
        onThemeChange: setTheme,
        theme,
    };
    const loginTabProps = {
        hasSession,
        isCheckingSession,
        onCheckSession: handleCheckSession,
        onClearSession: handleClearSession,
        outputState: loginOutputState,
        requestStatus: requestStatusText,
        accessToken,
        sessionImportedAt,
        sessionStatus: sessionStatusText,
    };
    const plotlyTabProps = {
        activePlotlyGroupLabel: activePlotlyGroup.label,
        activePlotlyRangeOptions,
        activePlotlyReferences,
        activePlotlySubsensorOptions,
        hasSession,
        isRequestingData,
        meters: Object.keys(activePlotlyGroup.meters),
        onRequestExample: handleExampleRequest,
        onSelectedGroupChange: setSelectedGroup,
        onSelectedMeterChange: setSelectedMeter,
        onSelectedRangeChange: setSelectedRange,
        onSelectedReferenceChange: setSelectedReference,
        onSelectedSubsensorChange: setSelectedSubsensor,
        outputMode,
        plotlyFigure,
        plotlyGroupOptions: PLOTLY_GROUP_OPTIONS,
        plotlyRequest,
        plotlyRequestMeter,
        selectedGroup,
        selectedMeter,
        selectedRange,
        selectedReference,
        selectedSensorDescription,
        selectedSubsensor,
    };
    const timeSeriesTabProps = {
        deviceMetadata: timeSeriesDeviceMetadata,
        deviceSensor: timeSeriesDeviceSensor,
        deviceSensorCatalog: timeSeriesDeviceSensorCatalog,
        hasSession,
        isRequestingSeries,
        onRequestTimeSeries: handleTimeSeriesRequest,
        onSelectedTimeSeriesAvailabilityChange: setSelectedTimeSeriesAvailability,
        onSelectedTimeSeriesGroupChange: setSelectedTimeSeriesGroup,
        onSelectedTimeSeriesGroupFilterChange: setSelectedTimeSeriesGroupFilter,
        onSelectedTimeSeriesMeterChange: setSelectedTimeSeriesMeter,
        onSelectedTimeSeriesSubsensorChange: setSelectedTimeSeriesSubsensor,
        onSelectedTimeSeriesVirtualDeviceGroupByChange: setSelectedTimeSeriesVirtualDeviceGroupBy,
        onTimeSeriesFromChange: setTimeSeriesFrom,
        onTimeSeriesTargetChange: setTimeSeriesTarget,
        onTimeSeriesToChange: setTimeSeriesTo,
        onTimeSeriesToModeChange: setTimeSeriesToMode,
        outputState: timeSeriesOutputState,
        selectedTimeSeriesAvailability,
        selectedTimeSeriesGroup,
        selectedTimeSeriesGroupFilter,
        selectedTimeSeriesMeter,
        selectedTimeSeriesSubsensor,
        selectedTimeSeriesVirtualDeviceGroupBy,
        timeSeriesFrom,
        timeSeriesRequestFrom: timeSeriesRequest.from,
        timeSeriesRequestTo: timeSeriesRequest.to,
        timeSeriesSensorTargets,
        timeSeriesTarget,
        timeSeriesTo,
        timeSeriesToMode,
        timeSeriesVirtualDevices,
    };
    const currentValueTabProps = {
        availability: currentValueAvailability,
        customer: currentValueCustomer,
        currentValueTarget,
        currentValueTargets,
        currentValueTimestamp,
        currentValueTimestampMode,
        deviceMetadata: currentValueDeviceMetadata,
        deviceSensor: currentValueDeviceSensor,
        deviceSensorCatalog: currentValueDeviceSensorCatalog,
        groupBy: currentValueGroupBy,
        groupFilter: currentValueGroupFilter,
        hasSession,
        isRequestingCurrentValue,
        onAddCurrentValueTarget: handleAddCurrentValueTarget,
        onRequestCurrentValue: handleCurrentValueRequest,
        onRemoveCurrentValueTarget: handleRemoveCurrentValueTarget,
        onAvailabilityChange: setCurrentValueAvailability,
        onCustomerChange: setCurrentValueCustomer,
        onDeviceChange: setCurrentValueDevice,
        onGroupByChange: setCurrentValueGroupBy,
        onGroupFilterChange: setCurrentValueGroupFilter,
        onSensorTargetChange: setCurrentValueSensorTarget,
        onTimeSeriesToChange: setCurrentValueTimestamp,
        onTimeSeriesToModeChange: setCurrentValueTimestampMode,
        outputState: currentValueOutputState,
        selectedDevice: currentValueDevice,
        selectedSensorTarget: currentValueSensorTarget,
        sensorTargets: currentValueSensorTargets,
        virtualDevices: currentValueVirtualDevices,
    };
    const intervalValueTabProps = {
        availability: intervalValueAvailability,
        customer: intervalValueCustomer,
        deviceMetadata: intervalValueDeviceMetadata,
        deviceSensor: intervalValueDeviceSensor,
        deviceSensorCatalog: intervalValueDeviceSensorCatalog,
        from: intervalValueFrom,
        hasSession,
        interval: intervalValueInterval,
        intervalValueTarget,
        intervalValueTargets,
        isRequestingIntervalValues,
        onAddIntervalValueTarget: handleAddIntervalValueTarget,
        onFromChange: setIntervalValueFrom,
        onIntervalChange: setIntervalValueInterval,
        onRemoveIntervalValueTarget: handleRemoveIntervalValueTarget,
        onRequestIntervalValues: handleIntervalValueRequest,
        onAvailabilityChange: setIntervalValueAvailability,
        onCustomerChange: setIntervalValueCustomer,
        onDeviceChange: setIntervalValueDevice,
        onGroupByChange: setIntervalValueGroupBy,
        onGroupFilterChange: setIntervalValueGroupFilter,
        onSensorTargetChange: setIntervalValueSensorTarget,
        onToChange: setIntervalValueTo,
        outputState: intervalValueOutputState,
        groupBy: intervalValueGroupBy,
        groupFilter: intervalValueGroupFilter,
        selectedDevice: intervalValueDevice,
        selectedSensorTarget: intervalValueSensorTarget,
        sensorTargets: intervalValueSensorTargets,
        to: intervalValueTo,
        virtualDevices: intervalValueVirtualDevices,
    };

    /* --------------------
       Route visibility (hide disabled demo and debug areas before rendering)
       -------------------- */
    const appRoutes = (
        <AppRoutes
            key={sensorCatalogVersion}
            currentValueTabProps={currentValueTabProps}
            debugEntries={debugEntries}
            hasSession={hasSession}
            intervalValueTabProps={intervalValueTabProps}
            loginPageProps={loginPageProps}
            loginTabProps={loginTabProps}
            plotlyTabProps={plotlyTabProps}
            theme={theme}
            timeSeriesTabProps={timeSeriesTabProps}
            toDebugString={toDebugString}
        />
    );
    const shouldBlockCurrentRoute =
        (!isDemoSectionEnabled && (
            APP_ROUTE_TABS.demo.includes(activeTab)
        )) ||
        (!isToolsSectionEnabled && (
            APP_ROUTE_TABS.tools.includes(activeTab) || APP_ROUTE_TABS.exampleDashboard.includes(activeTab)
        )) ||
        (!isDebugSectionEnabled && APP_ROUTE_TABS.debug.includes(activeTab));
    const shouldRedirectToLogin = isSessionStateReady && !hasSession && !isPublicRoute;
    const visibleAppRoutes = shouldRedirectToLogin
        ? <Navigate to={APP_ROUTES.login} replace />
        : shouldBlockCurrentRoute
        ? <Navigate to={hasSession ? APP_ROUTES.home : APP_ROUTES.login} replace />
        : appRoutes;

    /* --------------------
       Application shell (providers, global modals, and public or authenticated layout)
       -------------------- */
    return (
        <SessionProvider
            accessToken={accessToken}
            isSessionExpired={isSessionExpired}
            onSessionExpired={handleSessionExpired}
            username={sessionUsername}
        >
            <DashboardLayoutProvider
                desktopBreakpoint={dashboardDesktopBreakpoint}
                setDesktopBreakpoint={setDashboardDesktopBreakpoint}
            >
            <OverviewDisplayProvider showKpis={isAdvancedViewEnabled}>
            <TechnicalDetailsProvider showTechnicalDetails={isTechnicalDetailsEnabled}>
            <StatusModal
                isOpen={statusModal.isOpen}
                message={statusModal.message}
                onClose={statusModal.close}
                title={statusModal.title}
            />

            {shouldRedirectToLogin ? visibleAppRoutes : isLoginPage || (!hasSession && isPublicInfoPage) ? (
                <main className={`app-shell ${isDashboardPage ? 'app-shell--dashboard' : ''}`}>
                    <section
                        className={`app-content--public px-4 py-6 lg:px-6 ${
                            isLoginPage ? 'app-content--public-centered' : ''
                        }`}
                        aria-label={t('Workspace')}
                    >
                        {visibleAppRoutes}
                    </section>
                    <AppFooter hasSession={hasSession} />
                </main>
            ) : (
                <main className={`app-shell ${isDashboardPage ? 'app-shell--dashboard' : ''}`}>
                    <AppNavbar
                        accountActive={activeTab === 'login'}
                        currentPageBreadcrumbItems={currentPageBreadcrumbItems}
                        dashboardMenuItems={dashboardMenuItems}
                        debugMenuItems={debugMenuItems}
                        demoMenuItems={demoMenuItems}
                        toolsMenuItems={toolsMenuItems}
                        errorLogCount={errorLogCount}
                        hasSession={hasSession}
                        isDebugSectionEnabled={isDebugSectionEnabled}
                        isDashboardMenuActive={isDashboardMenuActive}
                        isDemoSectionEnabled={isDemoSectionEnabled}
                        isTechnicalDetailsEnabled={isTechnicalDetailsEnabled}
                        isToolsSectionEnabled={isToolsSectionEnabled}
                        isDebugMenuActive={isDebugMenuActive}
                        isDemoMenuActive={isDemoMenuActive}
                        isToolsMenuActive={isToolsMenuActive}
                        isAdvancedViewEnabled={isAdvancedViewEnabled}
                        language={language}
                        loginStatusLabel={loginStatusLabel}
                        sessionUsername={sessionUsername}
                        onClearSession={handleClearSession}
                        onDebugSectionEnabledChange={setIsDebugSectionEnabled}
                        onDemoSectionEnabledChange={setIsDemoSectionEnabled}
                        onTechnicalDetailsEnabledChange={setIsTechnicalDetailsEnabled}
                        onToolsSectionEnabledChange={setIsToolsSectionEnabled}
                        onAdvancedViewEnabledChange={setIsAdvancedViewEnabled}
                        onThemeChange={setTheme}
                        setLanguage={setLanguage}
                        showDeveloperOptions={canAccessDeveloperOptions}
                        theme={theme}
                    />

                    <div className={`app-main ${isDashboardPage ? 'app-main--dashboard' : ''} ${dashboardMainBreakpointClassName}`}>
                        <section
                            className={`app-content px-4 py-4 lg:px-6 ${
                                isDashboardPage ? 'app-content--dashboard' : 'app-content--default'
                            } ${dashboardContentBreakpointClassName}`}
                            aria-label={t('Workspace')}
                        >
                            {visibleAppRoutes}
                        </section>
                        <AppFooter hasSession={hasSession} />
                    </div>
                </main>
            )}
            </TechnicalDetailsProvider>
            </OverviewDisplayProvider>
            </DashboardLayoutProvider>
        </SessionProvider>
    );
}

export default App;
